import React from 'react';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import type { DocumentMetadata } from '~/components/communications/types';
import {
  buildConfiguredFieldsValuesYupShape,
  filterConfiguredFieldsValuesForType,
} from '~/components/Documents/configuredFieldsValues';
import { useCms } from '~/components/hooks/useCms';
import useOrganization from '~/components/OrganizationContext';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import type { OrganizationDocumentType } from '~/components/types/document-type';
import { getLocalDateToday } from '~/DateTimeUtils';
import { reportAxiosError, reportGenericException } from '~/Utils';
import { getNotifyUserMessage } from '~/Utils/errors/notifyUserMessage';

import UploadDocumentInner from './UploadDocumentsInner/UploadDocumentsInner';
import type { FileData, PartialValuesFormik, UploadSubmitOverride } from './types';
import { documentFieldsValidation } from './utils';

const getUploadDocumentInitialValues = (claim?: ClaimPolymorph, exposure_ids?: number[], allowNullClaimId = false) => ({
  claim_id: allowNullClaimId && !claim ? undefined : claim?.id,
  exposure_ids,
  type: '',
  document_name: '',
  document_date: getLocalDateToday(),
  document_date_received: getLocalDateToday(),
  summary: '',
  file: undefined,
  last_modified: getLocalDateToday(),
  files: [],
  configured_fields_values: {},
});

interface UploadDocumentProps {
  claim?: ClaimPolymorph;
  allowedDocumentTypes?: string[];
  onCancel: () => void;
  onSubmitDocument: (document: DocumentMetadata) => void;
  showFileBoxOnly?: boolean;
  initialValues?: {
    exposure_ids?: number[];
    type?: string;
  };
  alternativeUploadUrl?: string;
  uploadOutsideOfClaim?: boolean;
  trackAlt?: string;
  // Generic wrapper parameters
  banner?: React.ReactNode;
  customValidation?: Yup.AnyObjectSchema;
  onSubmitOverride?: UploadSubmitOverride;
}

const UploadDocument: React.FC<UploadDocumentProps> = ({
  claim,
  allowedDocumentTypes,
  onCancel,
  onSubmitDocument,
  showFileBoxOnly,
  initialValues,
  alternativeUploadUrl,
  uploadOutsideOfClaim,
  trackAlt,
  // Generic wrapper parameters
  banner,
  customValidation,
  onSubmitOverride,
}) => {
  const { user } = useCms();
  const { documentTypesDict } = useOrganization() as { documentTypesDict: Record<string, OrganizationDocumentType> };
  const urlPrefix = alternativeUploadUrl ? alternativeUploadUrl : `/api/v1/claims/${claim?.id}/documents`;

  const handleSubmitUploadedFiles = async (
    values: PartialValuesFormik,
    {
      setSubmitting,
      setFieldValue,
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
      setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
    }
  ) => {
    setSubmitting(true);

    try {
      // If there's a submit override function, use it instead of default logic
      if (onSubmitOverride) {
        await onSubmitOverride(values);
        onCancel(); // Call onCancel after successful override
        return;
      }

      // Default submit logic
      const getDocumentName = (file: FileData, i: number) => {
        if (values['document_name']) {
          return (values?.files?.length || 0) === 1 ? values['document_name'] : `${values['document_name']} (${i + 1})`;
        }
        return file?.name || file.file.name;
      };

      const selectedDocType = values.type ? documentTypesDict?.[values.type] : null;
      const filteredConfiguredValues = filterConfiguredFieldsValuesForType(
        values.configured_fields_values,
        selectedDocType
      );

      const documents = [];

      for (let i = 0; i < (values?.files?.length ?? 0); i++) {
        try {
          const fileData = values?.files?.[i];
          const file = fileData?.file;

          const storedFileId = fileData?.stored_file_id;
          const storageFileName = fileData?.storage_file_name;
          const documentName = getDocumentName(fileData, i);
          setFieldValue(`files[${i}].document_name`, documentName);

          const payload = {
            exposure_ids: values['exposure_ids'],
            last_modified: values['last_modified'],
            type: values['type'],
            summary: values['summary'],
            file_size: file?.size,
            document_name: documentName,
            document_date: values['document_date'],
            document_date_received: values['document_date_received'],
            stored_file_id: storedFileId,
            storage_filename: storageFileName,
            file_type: file?.type,
            file_name: file?.name,
            configured_fields_values: filteredConfiguredValues,
          };

          documents.push(payload);
        } catch (error) {
          const axiosError = error as AxiosError;
          const errorMessage = getNotifyUserMessage(error) ?? axiosError?.response?.data?.message;

          setFieldValue(`files[${i}].isError`, true);
          setFieldValue(`files[${i}].errorMessage`, errorMessage);
          reportGenericException(error);
        }
      }

      const { data } = await axios.post(`${urlPrefix}/bulk_update_document_stored_files`, {
        documents,
      });

      // NGTPA-17165 TODO: change onSubmitDocument to receive multiple documents ids
      onSubmitDocument(data?.documents[0]);
      onCancel();
    } catch (error) {
      await reportAxiosError(error);
      setSubmitting(false);
    }
  };

  const relatedExposure = !uploadOutsideOfClaim
    ? claim?.exposures?.find((exposure) => exposure?.handling_adjuster_id === user.id)
    : null;

  // `customValidation` is an intentional full override of the default schema,
  // not an extension. The Clive-only upload path passes
  // CLIVE_FILES_ONLY_VALIDATION to skip the type / exposure_ids / document_name
  // requireds (those fields are hidden when showFileBoxOnly is true, so the
  // default `required()` rules would block submission). Concatenating instead
  // of replacing breaks that flow — see PR #9284 review.
  //
  // The default branch is wrapped in Yup.lazy so the configured-fields schema
  // rebuilds against the currently-selected doc-type on every validation pass,
  // mirroring SubOrganizationDialog / FNOL.
  const validationSchema =
    customValidation ||
    Yup.lazy((values: PartialValuesFormik) => {
      const selectedDocType = values?.type ? documentTypesDict?.[values.type] : null;
      return Yup.object().shape({
        ...documentFieldsValidation({
          isClaimIdRequired: !uploadOutsideOfClaim,
          includeFiles: true,
        }),
        configured_fields_values: buildConfiguredFieldsValuesYupShape(selectedDocType),
      });
    });

  return (
    <Formik
      initialValues={{
        ...getUploadDocumentInitialValues(
          uploadOutsideOfClaim ? undefined : claim,
          [relatedExposure?.id ?? 0],
          !!uploadOutsideOfClaim
        ),
        // The caller's seeds win over the defaults above — spreading them first made
        // `initialValues` inert, because every key it may carry has a default here.
        ...initialValues,
      }}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting, setFieldValue }) => {
        await handleSubmitUploadedFiles(values, { setSubmitting, setFieldValue });
      }}
      enableReinitialize
    >
      <UploadDocumentInner
        claim={claim}
        onCancel={onCancel}
        urlPrefix={urlPrefix}
        allowedDocumentTypes={allowedDocumentTypes}
        showFileBoxOnly={showFileBoxOnly}
        trackAlt={trackAlt}
        banner={banner}
      />
    </Formik>
  );
};

export default UploadDocument;
